import { useRef } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useScrollProgress } from './hooks/useScrollProgress';

interface TimelineStep {
  number: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
}

interface AnimatedTimelineProps {
  steps: TimelineStep[];
  title?: string;
  id?: string;
}

const AnimatedTimeline = ({ steps, title, id }: AnimatedTimelineProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const progress = useScrollProgress(sectionRef);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1] as const
      }
    }
  };

  return (
    <section
      id={id}
      ref={sectionRef}
      className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    >
      {/* Floating gradient orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div 
          className="absolute top-20 left-[10%] w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, 50, 0],
            y: [0, 30, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-40 right-[15%] w-80 h-80 bg-accent/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, -40, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container mx-auto relative z-10">
        {title && (
          <motion.h2 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-12 sm:mb-16 md:mb-20 text-foreground px-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {title}
          </motion.h2>
        )}

        <div className="max-w-4xl mx-auto relative">
          {/* Animated vertical line with gradient */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-border/50 rounded-full overflow-hidden">
            <motion.div 
              className="absolute top-0 left-0 w-full bg-gradient-to-b from-primary via-accent to-primary rounded-full"
              style={{
                height: `${progress * 100}%`
              }}
              animate={{
                boxShadow: [
                  '0 0 10px hsl(var(--primary) / 0.3)',
                  '0 0 20px hsl(var(--primary) / 0.5)',
                  '0 0 10px hsl(var(--primary) / 0.3)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>

          {/* Timeline steps */}
          <motion.div 
            className="space-y-16 sm:space-y-20 md:space-y-24"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {steps.map((step, index) => {
              const stepProgress = Math.max(0, Math.min(1, (progress - (index / steps.length)) * steps.length));
              const isActive = stepProgress > 0.3;
              const isComplete = stepProgress > 0.8;

              return (
                <motion.div
                  key={index}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:justify-start' : 'md:justify-end'
                  }`}
                  variants={stepVariants}
                >
                  {/* Step circle with icon */}
                  <motion.div 
                    className={`absolute left-6 sm:left-8 md:left-1/2 md:-translate-x-1/2 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center z-10 transition-all duration-500 ${
                      isActive 
                        ? 'bg-gradient-to-br from-primary to-accent border-4 border-primary/50 shadow-premium-lg' 
                        : 'bg-background border-4 border-border'
                    }`}
                    animate={isActive ? {
                      boxShadow: [
                        '0 0 20px hsl(var(--primary) / 0.3)',
                        '0 0 40px hsl(var(--primary) / 0.5)',
                        '0 0 20px hsl(var(--primary) / 0.3)'
                      ]
                    } : {}}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {isComplete ? (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                      >
                        <Check className="w-6 h-6 sm:w-8 sm:h-8 text-primary-foreground" strokeWidth={3} />
                      </motion.div>
                    ) : step.icon ? (
                      <motion.div
                        className={isActive ? 'text-primary-foreground' : 'text-muted-foreground'}
                        animate={isActive ? { 
                          scale: [1, 1.1, 1],
                          rotate: [0, 5, -5, 0]
                        } : {}}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        {step.icon}
                      </motion.div>
                    ) : (
                      <span className={`text-xl sm:text-2xl font-bold transition-colors duration-500 ${
                        isActive ? 'text-primary-foreground' : 'text-muted-foreground'
                      }`}>
                        {step.number}
                      </span>
                    )}
                  </motion.div>

                  {/* Content card */}
                  <motion.div 
                    className={`w-full pl-20 sm:pl-24 md:pl-0 md:w-[45%] ${
                      index % 2 === 0 ? 'md:pr-16' : 'md:pl-16'
                    }`}
                    whileHover={{ 
                      scale: 1.02,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <div 
                      className={`relative bg-surface/50 backdrop-blur-md p-6 sm:p-8 rounded-xl border transition-all duration-500 group ${
                        isActive 
                          ? 'border-primary/50 shadow-premium-md bg-surface/70' 
                          : 'border-border/50 hover:border-accent/50'
                      } ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}
                    >
                      {/* Gradient border effect on hover */}
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/0 via-accent/20 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                      
                      <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-2 sm:mb-3 relative z-10">
                        {step.title}
                      </h3>
                      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed relative z-10">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AnimatedTimeline;
